import { Router, Routes } from '@angular/router';
import { ProvisionedDevices } from './modules/provisioned-devices/provisioned-devices.component';
import { DeviceOverviewComponent } from './modules/provisioned-devices/device-overview/device-overview.component';
import { DeviceConfiguration } from './modules/device-configuration/device-configuration';
import { OrganizationGuard } from './core/auth/organization.guard';
import { hasRoleGuard } from './core/auth/role.guard';
import { Role } from './core/auth/models/role';
import { UserManagementComponent } from './modules/user-management/user-management.component';
import { FileManagerComponent } from './modules/file-manager/file-manager.component';
import { OperationsComponent } from './modules/operation-profile/operations.component';
import { LayoutManagementComponent } from './modules/layout-management/layout-management.component';
import { MonitoringComponent } from './modules/monitoring/monitoring.component';
import { AlertsComponent } from './modules/alerts/alerts.component';
import { SoftwareUpdatesComponent } from './modules/software-updates/software-updates.component';
import { SoftwareUpdateDetailComponent } from './modules/software-updates/detail/software-update-detail.component';
import { TerminalPageComponent } from './modules/terminal-page/terminal-page.component';
import { TenantSettingsComponent } from './modules/tenant-settings/tenant-settings.component';
import { ContentComponent } from './modules/content/content.component';
import { UserPreferencesComponent } from './modules/tenant-settings/user-preferences.component';
import { AdminGuard } from './core/auth/admin.guard';
import { AdminTenantsComponent } from './modules/admin/tenants/tenants.component';
import { AdminTenantDetailComponent } from './modules/admin/tenants/tenant-detail/tenant-detail.component';
import { AdminTenantOverviewComponent } from './modules/admin/tenants/tenant-detail/tenant-overview.component';
import { AdminTenantUsersComponent } from './modules/admin/tenants/tenant-users/tenant-users.component';
import { AdminUserDetailComponent } from './modules/admin/tenants/tenant-users/user-detail.component';
import { AdminUsersComponent } from './modules/admin/users/users.component';
import { AdminOperationTemplatesComponent } from './modules/admin-operation-templates/admin-operation-templates.component';
import { ContextService } from './core/context.service';
import { NoOrganizationPage } from './core/errors/pages/no-organization/no-organization.component';
import { inject } from '@angular/core';
import { filter, map, take } from 'rxjs';
import { NotFoundPage } from './core/errors/pages/not-found/not-found.component';
import { LoadFailedPage } from './core/errors/pages/load-failed/load-failed.component';
import { readStoredPortal, TENANT_HOME_PATH } from './core/portal.service';
import { Organization } from './core/models/organization.model';
import { PROVIDER_ROUTES } from './modules/providers/providers.routes';

export const routes: Routes = [
    // Error routes. bareLayout drops the app shell (topbar/sidebar): these
    // pages render full-screen because their context is broken or absent —
    // org-scoped nav links would hang or lie. AppComponent reads the flag.
    { path: 'no-organization', component: NoOrganizationPage, title: 'pageTitle.noOrganization', data: { bareLayout: true } },
    { path: 'not-found', component: NotFoundPage, title: 'pageTitle.notFound', data: { bareLayout: true } },
    { path: 'load-failed', component: LoadFailedPage, title: 'pageTitle.loadFailed', data: { bareLayout: true } },

    // Admin back-office subtree. Guarded once at the root by AdminGuard
    // (SUPER_ADMIN) — every child inherits it, so new admin features need no
    // guard of their own. Not organization-scoped: admin manages all tenants.
    {
        path: 'admin',
        canActivate: [AdminGuard],
        children: [
            { path: '', redirectTo: 'tenants', pathMatch: 'full' },
            // Tenants drill-down: tenants → tenant → users → user. Each level
            // carries data.breadcrumb, so the trail (and its links) derives
            // from this tree — see BreadcrumbService.
            {
                path: 'tenants',
                title: 'pageTitle.tenants',
                data: { breadcrumb: 'breadcrumb.tenants' },
                children: [
                    { path: '', component: AdminTenantsComponent },
                    {
                        path: ':tenantId',
                        data: { breadcrumb: { param: 'tenantId' } },
                        children: [
                            // Tenant detail shell: Overview | Users tabs. Both
                            // tabs render inside the shell's outlet; the user
                            // detail below stays a full page outside it.
                            {
                                path: '',
                                component: AdminTenantDetailComponent,
                                children: [
                                    { path: '', pathMatch: 'full', component: AdminTenantOverviewComponent },
                                    {
                                        path: 'users',
                                        component: AdminTenantUsersComponent,
                                        title: 'pageTitle.users',
                                        data: { breadcrumb: 'breadcrumb.users' },
                                    },
                                ],
                            },
                            {
                                path: 'users',
                                title: 'pageTitle.users',
                                data: { breadcrumb: 'breadcrumb.users' },
                                children: [
                                    {
                                        path: ':userId',
                                        component: AdminUserDetailComponent,
                                        data: { breadcrumb: { param: 'userId' } },
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            { path: 'users', component: AdminUsersComponent, title: 'pageTitle.users' },
            {
                path: 'operation-templates',
                component: AdminOperationTemplatesComponent,
                title: 'pageTitle.operationTemplates',
                data: { breadcrumb: 'breadcrumb.operationTemplates' },
            },
            // Providers drill-down: providers → provider (Feeds | Details tabs) →
            // feed, with a separate route tree per provider type (siri, gtfs,
            // generic) — see providers.routes.ts.
            {
                path: 'providers',
                title: 'pageTitle.providers',
                data: { breadcrumb: 'breadcrumb.providers' },
                children: PROVIDER_ROUTES,
            },
            // Detail nests under a componentless parent so each level can
            // carry its own data.breadcrumb, same as tenants/devices above.
            {
                path: 'software-updates',
                title: 'pageTitle.softwareUpdates',
                data: { breadcrumb: 'breadcrumb.softwareUpdates' },
                children: [
                    { path: '', component: SoftwareUpdatesComponent },
                    {
                        path: ':id',
                        component: SoftwareUpdateDetailComponent,
                        data: { breadcrumb: { param: 'id' } },
                    },
                ],
            },
        ]
    },

    // Organization-scoped routes
    {
        path: ':organizationName',
        canActivate: [OrganizationGuard],
        children: [
            { path: '', redirectTo: TENANT_HOME_PATH, pathMatch: 'full' },
            { path: 'monitoring', component: MonitoringComponent, title: 'pageTitle.monitoring' },
            // Viewer-readable; write actions inside the page are gated on
            // tenant-admin by the component itself.
            { path: 'alerts', component: AlertsComponent, title: 'pageTitle.alerts' },
            // Detail routes nest under a componentless parent so each level
            // can carry its own data.breadcrumb (URLs are unchanged).
            {
                path: 'devices',
                title: 'pageTitle.devices',
                data: { breadcrumb: 'breadcrumb.devices' },
                children: [
                    { path: '', component: ProvisionedDevices },
                    {
                        path: ':clientId',
                        component: DeviceOverviewComponent,
                        data: { breadcrumb: { param: 'clientId' } },
                    },
                ],
            },
            { path: 'device-configurations', component: DeviceConfiguration, title: 'pageTitle.deviceConfigurations' },
            {
                path: 'user-management',
                component: UserManagementComponent,
                title: 'pageTitle.userManagement',
                canActivate: [hasRoleGuard(Role.TENANT_ADMIN, Role.SUPER_ADMIN)],
            },
            { path: 'operations', component: OperationsComponent, title: 'pageTitle.operations' },
            { path: 'content', component: ContentComponent, title: 'pageTitle.content' },
            { path: 'files', component: FileManagerComponent, title: 'pageTitle.files' },
            { path: 'layouts', component: LayoutManagementComponent, title: 'pageTitle.layouts' },
            {
                path: 'settings',
                children: [
                    { path: '', component: TenantSettingsComponent, title: 'pageTitle.settings' },
                    { path: 'preferences', component: UserPreferencesComponent, title: 'pageTitle.preferences' },
                ],
            },
            {
                path: 'terminal',
                title: 'pageTitle.terminal',
                data: { breadcrumb: 'breadcrumb.terminal' },
                children: [
                    { path: '', component: TerminalPageComponent },
                    {
                        path: ':clientId',
                        component: TerminalPageComponent,
                        data: { breadcrumb: { param: 'clientId' } },
                    },
                ],
            },
        ]
    },
    {
        path: '',
        pathMatch: 'full',
        canActivate: [
            () => {
                const contextService = inject(ContextService);
                const router = inject(Router);

                // Gate on context load (not org presence) so org-less
                // super-admins don't hang here. Super-admins land back in the
                // portal they last worked in (back-office by default); a
                // tenant user lands in their org's default; a user with
                // neither role nor org goes to the no-organization page
                // rather than a broken URL.
                return contextService.loaded$.pipe(
                    filter(loaded => loaded),
                    take(1),
                    map(() => {
                        if (contextService.hasRole(Role.SUPER_ADMIN)) {
                            const tenantLanding = preferredTenantLanding(contextService);
                            return tenantLanding
                                ? router.createUrlTree([tenantLanding.name, TENANT_HOME_PATH])
                                : router.createUrlTree(['admin']);
                        }
                        const organization = contextService.getOrganization();
                        return organization
                            ? router.createUrlTree([organization.name, TENANT_HOME_PATH])
                            : router.createUrlTree(['no-organization']);
                    })
                );
            }
        ],
        children: []
    }
];

// A super-admin lands in the tenant portal only when that's their remembered
// choice AND an organization can be resolved to land in; otherwise the admin
// back-office. Only the bare-"/" redirect consults the stored preference —
// an explicitly typed or bookmarked URL always wins.
function preferredTenantLanding(contextService: ContextService): Organization | null {
    if (readStoredPortal() !== 'tenant') {
        return null;
    }
    return contextService.getOrganization()
        ?? contextService.getPersistedOrganization()
        ?? contextService.getOrganizations()[0]
        ?? null;
}
